import { getBrowserLocalStorage } from '@instamenu/browser-storage';
import { makeAutoObservable, toJS } from 'mobx';
import { omit } from 'lodash-es';
import { UpdateUserMutation } from './mutations.generated';
import { DeliveryAddressAttributes } from '@instamenu/data-access';
import { formatAddress } from '@instamenu/formatters';

export type AddressJSON = Omit<
  NonNullable<NonNullable<UpdateUserMutation['updateUser']>['user']>['deliveryAddresses'][0],
  'id' | 'key'
> & { id?: string; key?: string };

const localStorageKey = 'deliveryAddress';

const byKey = new Map<string, Address>();

export class Address {
  json: AddressJSON & { key: string };
  private constructor(json: AddressJSON) {
    this.json = { ...json, key: json.key ?? json.id ?? crypto.randomUUID() };
    makeAutoObservable(this);
  }
  static fromJSON = (json: AddressJSON) => {
    const key = json.key ?? json.id;
    let instance = key ? byKey.get(key) : undefined;
    if (!instance) {
      instance = new Address(json);
    } else {
      instance.updateFromJson(json);
    }
    byKey.set(instance.key, instance);
    return instance;
  };
  get id() {
    return this.json.id;
  }
  get key() {
    return this.json.id ?? this.json.key;
  }
  get location() {
    return this.json.location;
  }
  get formattedAddress() {
    return formatAddress(this.json);
  }
  get line1() {
    return this.json.street + (this.json.street2 ? ', ' + this.json.street2 : '');
  }
  get line2() {
    return `${this.json.city ? this.json.city + ',' : ''} ${this.json.state} ${this.json.zip}`;
  }
  get specialInstructions() {
    return this.json.specialInstructions;
  }
  asDeliveryAddressAttributes(): DeliveryAddressAttributes {
    return {
      ...omit(this.json, ['location', '__typename']),
      locationAttributes: this.json.location
        ? {
            lat: this.json.location.lat,
            lon: this.json.location.lon,
          }
        : undefined,
    };
  }

  asRestaurantAddressAttributes() {
    const attributes = {
      ...omit(this.json, ['location', '__typename', 'formattedAddress', 'id', 'key']),
      locationAttributes: this.json.location
        ? {
            lat: this.json.location.lat,
            lon: this.json.location.lon,
          }
        : undefined,
    };
    return attributes;
  }

  updateFromJson(json: Partial<AddressJSON>) {
    this.json = { ...this.json, ...json };
  }

  static fromLocalStorage() {
    try {
      const str = getBrowserLocalStorage()?.getItem(localStorageKey);
      if (str) return Address.fromJSON(JSON.parse(str));
    } catch {
      // it's okay if this fails
    }
    return;
  }

  toLocalStorage() {
    getBrowserLocalStorage()?.setItem(localStorageKey, JSON.stringify(toJS(this.json)));
  }
  static clearLocalStorage() {
    getBrowserLocalStorage()?.removeItem(localStorageKey);
  }
}
